import React, { useEffect, useState } from "react";
import { Heading } from "../../design/heading";
import { useIncome } from "../../contexts/IncomeContext";
import { useMatter } from "../../contexts/MatterContext";
import { useCreateIncomeDialog } from "../../providers/CreateIncomeDialogProvider";
import IncomeListItem from "./components/IncomeListItem";
import ErrorView from "../../components/ErrorView";
import LoadingView from "../../components/LoadingView";
import NoMatterView from "../../components/NoMatterView";
import { Button } from "../../design/button";
import { Navbar, NavbarItem } from "../../design/navbar";
import IncomeListSidebar from "./components/IncomeListSidebar";
import { IncomeType } from "../../models/IncomeType";
import { Participant } from "../../models/Participant";

const IncomeListPage: React.FC = () => {
  const { currentMatterId, partiesInvolved } = useMatter();
  const { incomes, loading, error } = useIncome();
  const { openDialog } = useCreateIncomeDialog();
  const [selectedParticipant, setSelectedParticipant] = useState<
    Participant["_id"] | undefined
  >(() => {
    return localStorage.getItem("selectedParticipant") || undefined;
  });
  const [selectedType, setSelectedType] = useState<IncomeType>("employment_w2");

  useEffect(() => {
    if (selectedParticipant !== undefined) {
      localStorage.setItem("selectedParticipant", selectedParticipant);
    } else {
      localStorage.removeItem("selectedParticipant");
    }
  }, [selectedParticipant]);

  if (!currentMatterId) return <NoMatterView />;
  if (loading.list) return <LoadingView title="Loading Incomes..." />;
  if (error.list) return <ErrorView error={error.list} />;

  // Calculate totals and group by type if needed
  const totalIncome = incomes.reduce(
    (sum, income) => sum + (income.amount || 0),
    0,
  );
  const filteredIncomes = (
    selectedType
      ? incomes.filter((income) => income.type === selectedType)
      : incomes
  ).filter((income) =>
    selectedParticipant
      ? income.participantId === selectedParticipant
      : !income.participantId,
  );

  return (
    <div className="flex flex-col w-full h-full">
      {/* Header */}
      <div className="flex items-center justify-between border-b">
        <Navbar>
          {partiesInvolved.map((participant) => (
            <NavbarItem
              key={participant._id}
              current={selectedParticipant === participant._id}
              onClick={() => setSelectedParticipant(participant._id)}
            >
              {participant.name}
            </NavbarItem>
          ))}
          <NavbarItem
            current={!selectedParticipant}
            onClick={() => setSelectedParticipant(undefined)}
          >
            Joint Income
          </NavbarItem>
        </Navbar>
        <div className="text-lg font-semibold">
          Total: ${totalIncome.toLocaleString()}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-1/4 p-4 overflow-y-auto border-r">
          <IncomeListSidebar
            onSelect={setSelectedType}
            selectedType={selectedType}
            participantId={selectedParticipant}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="flex items-end justify-between mb-4">
            <Heading className="text-2xl font-bold">Income Details</Heading>
            <Button
              onClick={() => openDialog(selectedParticipant, selectedType)}
              className="!px-4"
            >
              <span className="hidden sm:block">Add Income</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>

          {/* Example Detail Form */}
          <div className="space-y-4">
            {filteredIncomes.length > 0 ? (
              filteredIncomes.map((income) => (
                <IncomeListItem key={income._id} income={income} />
              ))
            ) : (
              <div className="text-center text-gray-500">
                No incomes found for this type.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomeListPage;
